import { useState } from 'react';
import { z } from 'zod';

import { Modal } from './Modal';
import { useCreateNotificationMutation } from '@/store/api/notificationApi';

interface AddNotificationModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess?: () => void;
  notificationCode?: string;
}

const notifictaionSchema = z.object({
  title: z.string().trim().min(1, 'Title is required').max(50, 'Max 50 characters'),
  message: z.string().trim().min(1, 'Message is required').max(200, 'Max 200 characters'),
  link: z
    .string()
    .trim()
    .refine(
      (val) => {
        if (!val) return true;

        try {
          new URL(val);
          return true;
        } catch {
          return false;
        }
      },
      {
        message: 'Enter a valid URL',
      }
    ),
});

type NotificationForm = z.infer<typeof notifictaionSchema>;
type FormErrors = Partial<Record<keyof NotificationForm | 'api', string>>;

const validateField = <K extends keyof NotificationForm>(
  key: K,
  value: NotificationForm[K],
  currentData: NotificationForm
) => {
  const result = notifictaionSchema.safeParse({
    ...currentData,
    [key]: value,
  });

  if (!result.success) {
    const fieldError = result.error.flatten().fieldErrors[key];
    return fieldError?.[0];
  }

  return undefined;
};

export const AddNotificationModal = ({
  isOpen,
  onClose,
  onSuccess,
  notificationCode = 'SYSTEM_CUSTOM',
}: AddNotificationModalProps) => {
  const [title, setTitle] = useState('');
  const [message, setMessage] = useState('');
  const [link, setLink] = useState('');
  const [errors, setErrors] = useState<FormErrors>({});
  const [createNotification, { isLoading }] = useCreateNotificationMutation();

  const resetForm = () => {
    setTitle('');
    setMessage('');
    setLink('');
    setErrors({});
  };

  const handleClose = () => {
    resetForm();
    onClose();
  };

  const handleSubmit = async () => {
    setErrors({});

    const result = notifictaionSchema.safeParse({
      title,
      message,
      link,
    });

    if (!result.success) {
      const fieldErrors = result.error.flatten().fieldErrors;
      const formattedErrors: FormErrors = {};

      (Object.keys(fieldErrors) as Array<keyof typeof fieldErrors>).forEach((key) => {
        const message = fieldErrors[key]?.[0];
        if (message) formattedErrors[key] = message;
      });

      setErrors(formattedErrors);
      return;
    }

    try {
      await createNotification({
        title: title.trim(),
        message: message.trim(),
        link: link.trim(),
        notificationCode,
      }).unwrap();

      onSuccess?.();
      handleClose();
    } catch (err: any) {
      const message = err?.data?.message || err?.data?.error || 'Something went wrong';
      setErrors({ api: Array.isArray(message) ? message[0] : message });
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={handleClose} title="Add New Notification">
      <div className="space-y-5">
        <div className="space-y-2">
          <label className="text-sm font-semibold text-[#62556B]">Title *</label>
          <input
            type="text"
            value={title}
            onChange={(e) => {
              const value = e.target.value;
              setTitle(value);
              const error = validateField('title', value, {
                title: value,
                message: message,
                link: link,
              });
              setErrors((prev) => ({ ...prev, title: error }));
            }}
            className="w-full px-4 py-3 bg-[#F8F5FA] rounded-xl text-[15px] focus:outline-none"
          />
          {errors.title && <p className="text-red-500 text-xs mt-1">{errors.title}</p>}
        </div>

        <div className="space-y-2">
          <label className="text-sm font-semibold text-[#62556B]">Message *</label>
          <textarea
            value={message}
            onChange={(e) => {
              const value = e.target.value;
              setMessage(value);
              const error = validateField('message', value, {
                title,
                message: value,
                link: link,
              });
              setErrors((prev) => ({ ...prev, message: error }));
            }}
            rows={4}
            className="w-full px-4 py-3 bg-[#F8F5FA] rounded-xl text-[15px] focus:outline-none resize-none"
          />
          {errors.message && <p className="text-red-500 text-xs mt-1">{errors.message}</p>}
        </div>

        <div className="space-y-2">
          <label className="text-sm font-semibold text-[#62556B]">Link</label>

          <input
            type="text"
            value={link}
            onChange={(e) => {
              const value = e.target.value;

              setLink(value);

              const error = validateField('link', value, {
                title,
                message,
                link: value,
              });

              setErrors((prev) => ({
                ...prev,
                link: error,
              }));
            }}
            className="w-full px-4 py-3 bg-[#F8F5FA] rounded-xl text-[15px] focus:outline-none resize-none"
            placeholder="https://example.com"
          />

          {errors.link && <p className="text-red-500 text-xs mt-1">{errors.link}</p>}
        </div>

        {errors.api && <p className="text-red-500 text-sm text-center">{errors.api}</p>}

        <div className="flex items-center justify-center gap-4 pb-4">
          <button
            onClick={handleClose}
            className="px-12 py-3 border border-[#1A0A30] rounded-xl text-[15px] font-bold text-[#1A0A30]"
          >
            Cancel
          </button>

          <button
            onClick={handleSubmit}
            disabled={isLoading}
            className="px-12 py-3 rounded-xl text-[15px] font-bold text-white bg-[#1A0A30]"
          >
            Create
          </button>
        </div>
      </div>
    </Modal>
  );
};
